/************************************************************************************************
 * Copyright TRUSST AI PTY LTD. All Rights Reserved.                                            *
 *                                                                                              *
 * Licensed under the TRUSST SOFTWARE LICENSE (the "License"). You may not use this file except *
 * in compliance with the "LICENSE" file accompanying this file. This file is distributed       *
 * on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, express or implied.       *
 *                                                                                              *
 * See the "License" file for the specific language governing permissions and limitations       *
 * under the License and limitations under the License.                                         *
 ***********************************************************************************************/

const hexToRgb = (hex: string): number[] => {
  const cleanHex = hex.replace('#', '');
  const bigint = parseInt(cleanHex, 16);
  // eslint-disable-next-line no-bitwise
  return [(bigint >> 16) & 255, (bigint >> 8) & 255, bigint & 255];
};

const getColorFromCSSVariable = (variable: string): number[] | null => {
  const value = getComputedStyle(document.documentElement).getPropertyValue(variable).trim();
  if (!value || !/^#([A-Fa-f0-9]{6})$/.test(value)) {
    return null;
  }
  return hexToRgb(value);
};

export const COLOR_THRESHOLDS = {
  errorToWarning: 80,
  warningToSuccess: 100,
};

export const getColorFromPercentage = (percentage: number): string | null => {
  const clampedPercentage = Math.max(0, Math.min(100, percentage));

  const errorColor = getColorFromCSSVariable('--mui-palette-error-main');
  const warningColor = getColorFromCSSVariable('--mui-palette-warning-main');
  const successColor = getColorFromCSSVariable('--mui-palette-success-main');

  if (!errorColor || !warningColor || !successColor) {
    return null;
  }

  let startColor: number[];
  let endColor: number[];
  let weight: number;

  if (clampedPercentage <= COLOR_THRESHOLDS.errorToWarning) {
    startColor = errorColor;
    endColor = warningColor;
    weight = clampedPercentage / COLOR_THRESHOLDS.errorToWarning;
  } else {
    startColor = warningColor;
    endColor = successColor;
    weight =
      (clampedPercentage - COLOR_THRESHOLDS.errorToWarning) /
      (COLOR_THRESHOLDS.warningToSuccess - COLOR_THRESHOLDS.errorToWarning);
  }

  const interpolatedColor = startColor.map((start, i) => Math.round(start + (endColor[i] - start) * weight));

  return `#${interpolatedColor.map((val) => val.toString(16).padStart(2, '0')).join('')}`;
};
